import { useState, useRef, useEffect } from 'react';
import { useFamilyContext } from '../../contexts/FamilyContext';
import { X, Lightbulb, Hammer, Star, BookOpen, Plus } from 'lucide-react';

export default function IdeaForm({ idea = null, onClose }) {
  const { addIdea, editIdea, family } = useFamilyContext();
  const titleInputRef = useRef(null);
  const descriptionInputRef = useRef(null);
  
  const [formData, setFormData] = useState({
    title: idea?.title || '',
    description: idea?.description || '',
    category: idea?.category || 'creative',
    targetKids: idea?.targetKids || [],
    links: idea?.links?.length > 0 ? idea.links.join('\n') : ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [titleFocused, setTitleFocused] = useState(false);
  const [descriptionExpanded, setDescriptionExpanded] = useState(!!idea?.description);
  const [showLinkInput, setShowLinkInput] = useState(!!idea?.links?.length);
  
  const mode = idea ? 'edit' : 'add';

  // Focus title input when modal opens
  useEffect(() => {
    if (titleInputRef.current) {
      setTimeout(() => titleInputRef.current?.focus(), 100);
    }
  }, []);

  // Auto-save functionality
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (formData.title.trim() || formData.description.trim()) {
        localStorage.setItem('ideaDraft', JSON.stringify(formData));
      }
    }, 1000);

    return () => clearTimeout(timeoutId);
  }, [formData]);

  // Load draft on mount if no existing idea
  useEffect(() => {
    if (!idea) {
      const draft = localStorage.getItem('ideaDraft');
      if (draft) {
        try {
          const parsedDraft = JSON.parse(draft);
          setFormData(parsedDraft);
          if (parsedDraft.description) setDescriptionExpanded(true);
          if (parsedDraft.links) setShowLinkInput(true);
        } catch {
          // Ignore parse errors
        }
      }
    }
  }, [idea]);

  const categories = [
    { value: 'creative', label: 'Creative', icon: <Lightbulb className="w-4 h-4" /> },
    { value: 'project', label: 'Project', icon: <Hammer className="w-4 h-4" /> },
    { value: 'learning', label: 'Learning', icon: <BookOpen className="w-4 h-4" /> },
    { value: 'experience', label: 'Activity', icon: <Star className="w-4 h-4" /> }
  ];

  // Get kid initials and color
  const getKidAvatar = (kid) => {
    const colors = {
      'violet': 'bg-violet-500',
      'pink': 'bg-pink-500',
      'azure': 'bg-indigo-500',
      'emerald': 'bg-indigo-700',
      'yellow': 'bg-yellow-500',
      'orange': 'bg-orange-500'
    };
    
    const initial = kid.name.charAt(0).toUpperCase();
    const color = colors[kid.color] || 'bg-stone-500';
    
    return { initial, color };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      if (!formData.title.trim()) {
        alert('Please enter an idea');
        return;
      }

      if (formData.targetKids.length === 0) {
        alert('Please select at least one kid');
        return;
      }

      const ideaData = {
        title: formData.title.trim(),
        description: formData.description.trim(),
        category: formData.category,
        targetKids: formData.targetKids,
        links: formData.links.trim() ? formData.links.split('\n').filter(link => link.trim()) : [],
        kidResponses: idea?.kidResponses || {}
      };

      if (mode === 'edit' && idea?.id) {
        await editIdea(idea.id, ideaData);
      } else {
        await addIdea(ideaData);
      }
      
      clearDraft();
      onClose();
    } catch (err) {
      console.error('Error saving idea:', err);
      alert('Error saving idea: ' + err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleKidToggle = (kidId) => {
    setFormData(prev => ({
      ...prev,
      targetKids: prev.targetKids.includes(kidId)
        ? prev.targetKids.filter(id => id !== kidId)
        : [...prev.targetKids, kidId]
    }));
  };

  const handleCategorySelect = (categoryValue) => {
    setFormData(prev => ({ ...prev, category: categoryValue }));
  };

  const handleTitleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (!descriptionExpanded) {
        setDescriptionExpanded(true);
        setTimeout(() => descriptionInputRef.current?.focus(), 100);
      }
    }
  };

  const clearDraft = () => {
    localStorage.removeItem('ideaDraft');
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-neutral-50 rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b sticky top-0 bg-neutral-50 z-10">
          <h2 className="text-xl font-bold text-stone-900">
            {mode === 'edit' ? 'Edit Idea' : 'New Idea'}
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-stone-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-stone-600" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Conversational Title Input */}
          <div>
            <p className="text-sm text-stone-600 mb-3">What's on your mind?</p>
            <div>
              <input
                ref={titleInputRef}
                type="text"
                value={formData.title}
                onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                onKeyPress={handleTitleKeyPress}
                onFocus={() => setTitleFocused(true)}
                onBlur={() => setTitleFocused(false)}
                className={`w-full px-6 py-4 text-lg border-2 rounded-xl transition-all duration-200 ${
                  titleFocused || formData.title 
                    ? 'border-azure-500 bg-indigo-50/50' 
                    : 'border-stone-200 hover:border-stone-300'
                } focus:outline-none focus:ring-0`}
                placeholder="Enter your idea..."
                autoComplete="off"
                required
              />
            </div>
            
            {/* Expandable Description */}
            {descriptionExpanded && (
              <div className="mt-4">
                <textarea
                  ref={descriptionInputRef}
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  rows={3}
                  className="w-full px-6 py-4 text-base border-2 rounded-xl transition-all duration-200 resize-none border-stone-200 hover:border-stone-300 focus:border-azure-500 focus:bg-indigo-50/50 focus:outline-none focus:ring-0"
                  placeholder="Add details (optional)..."
                  autoComplete="off"
                />
              </div>
            )}
            
            {!descriptionExpanded && formData.title && (
              <button
                type="button"
                onClick={() => {
                  setDescriptionExpanded(true);
                  setTimeout(() => descriptionInputRef.current?.focus(), 100);
                }}
                className="mt-3 text-sm text-stone-500 hover:text-stone-700 transition-colors"
              >
                + Add details
              </button>
            )}
          </div>

          {/* Category Tags */}
          <div>
            <div className="flex flex-wrap gap-2">
              {categories.map(category => (
                <button
                  key={category.value}
                  type="button"
                  onClick={() => handleCategorySelect(category.value)}
                  className={`inline-flex items-center px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
                    formData.category === category.value
                      ? 'bg-indigo-600 text-neutral-50 shadow-md transform scale-105'
                      : 'bg-stone-100 text-stone-700 hover:bg-stone-200'
                  }`}
                >
                  <span className="mr-2">{category.icon}</span>
                  {category.label}
                </button>
              ))}
            </div>
          </div>

          {/* Visual Kid Selection */}
          <div>
            <p className="text-sm font-medium text-stone-700 mb-3">For which kids?</p>
            <div className="flex flex-wrap gap-3">
              {family?.kids.map(kid => {
                const { initial, color } = getKidAvatar(kid);
                const isSelected = formData.targetKids.includes(kid.id);
                
                return (
                  <button
                    key={kid.id}
                    type="button"
                    onClick={() => handleKidToggle(kid.id)}
                    className={`relative transition-all duration-200 ${
                      isSelected ? 'transform scale-110' : 'opacity-60 hover:opacity-80'
                    }`}
                  >
                    <div className={`w-16 h-16 ${color} rounded-full flex items-center justify-center text-neutral-50 font-bold text-xl shadow-lg ${
                      isSelected ? 'ring-4 ring-offset-2 ring-azure-500' : ''
                    }`}>
                      {initial}
                    </div>
                    <span className="block text-xs font-medium text-stone-700 mt-1">{kid.name}</span>
                    {isSelected && (
                      <div className="absolute -top-1 -right-1 w-6 h-6 bg-indigo-700 rounded-full flex items-center justify-center">
                        <span className="text-neutral-50 text-xs">✓</span>
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Simplified Link Input */}
          <div>
            <p className="text-sm font-medium text-stone-700 mb-2">Add link (optional)</p>
            {showLinkInput ? (
              <div className="relative">
                <textarea
                  value={formData.links}
                  onChange={(e) => setFormData(prev => ({ ...prev, links: e.target.value }))}
                  rows={2}
                  className="w-full px-4 py-3 border border-stone-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-azure-500 focus:border-azure-500 resize-none"
                  placeholder="Paste a helpful link..."
                />
                <button
                  type="button"
                  onClick={() => {
                    setShowLinkInput(false);
                    setFormData(prev => ({ ...prev, links: '' }));
                  }}
                  className="absolute top-2 right-2 p-1 text-stone-400 hover:text-stone-600"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setShowLinkInput(true)}
                className="w-full px-4 py-3 border-2 border-dashed border-stone-300 rounded-xl text-stone-500 hover:border-stone-400 hover:text-stone-700 transition-all duration-200 flex items-center justify-center"
              >
                <Plus className="w-5 h-5 mr-2" />
                Add Link
              </button>
            )}
          </div>

          {/* Auto-save indicator */}
          {(formData.title.trim() || formData.description.trim()) && (
            <div className="flex items-center justify-center py-2">
              <div className="flex items-center text-xs text-stone-500">
                <div className="w-2 h-2 bg-indigo-600 rounded-full mr-2 animate-pulse"></div>
                Auto-saving...
              </div>
            </div>
          )}

          {/* Enhanced Submit Button */}
          <div className="pt-4">
            <button
              type="submit"
              disabled={isSubmitting || !formData.title.trim() || formData.targetKids.length === 0}
              className="w-full inline-flex items-center justify-center px-8 py-4 bg-indigo-800 text-neutral-50 font-semibold text-lg rounded-xl hover:bg-emerald-700 transform hover:scale-[1.02] transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none shadow-lg"
            >
              {isSubmitting ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-neutral-50 mr-3"></div>
                  {mode === 'edit' ? 'Saving...' : 'Adding...'}
                </>
              ) : (
                <>
                  <span className="mr-2">✨</span>
                  {mode === 'edit' ? 'Save Changes' : 'Add Idea'}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}